/**
 * Getting a meeting *into* the product: send the recorder somewhere, or upload
 * something that was recorded elsewhere.
 *
 * Both are the same question — "record this" — asked before and after the fact,
 * which is why they sit together behind one control rather than as two features
 * that happen to both produce a meeting.
 */
import { apiGet, apiSend, apiUrl } from '@/lib/api'

/* ---------- Send the recorder ---------- */

export interface SendBotResult {
  botId: string
  /** Which recorder went: the tenant's own Teams identity, or the fallback. */
  provider: 'native_teams' | 'recall'
  /** True when the native path could not take it and Recall was used instead. */
  fellBack: boolean
  title: string | null
}

/**
 * A meeting link, roughly.
 *
 * Deliberately permissive: the service does the real validation and knows about
 * platforms this list does not. The check here exists to catch a pasted email
 * body or a half-copied URL before it becomes a round trip.
 */
export function looksLikeMeetingUrl(value: string): boolean {
  const text = value.trim()
  if (!/^https?:\/\//i.test(text)) return false
  try {
    new URL(text)
    return true
  } catch {
    return false
  }
}

export async function sendBot(input: {
  url: string
  title?: string
  participantEmails?: string[]
}): Promise<SendBotResult> {
  const body = await apiSend<{
    bot_id: string
    bot_provider: 'native_teams' | 'recall'
    fallback_used: boolean
    meeting_title: string | null
  }>('POST', '/meetings/bot/send', {
    meeting_url: input.url.trim(),
    ...(input.title?.trim() ? { meeting_title: input.title.trim() } : {}),
    participant_emails: input.participantEmails ?? [],
  })

  return {
    botId: body.bot_id,
    provider: body.bot_provider,
    fellBack: body.fallback_used,
    title: body.meeting_title,
  }
}

/* ---------- Upload a recording ---------- */

export type UploadPhase = 'preparing' | 'uploading' | 'processing'

interface DirectUploadStart {
  meeting_id: string
  bot_id: string
  upload_id: string
  s3_key: string
  upload_url: string
  upload_headers?: Record<string, string>
}

/**
 * Uploads a recording, straight to object storage.
 *
 * Three steps: ask the service for a signed URL, PUT the file at storage, then
 * tell the service it landed. The file never passes through the API, which is
 * what makes an hour of video viable — routing it through the backend would
 * mean holding it in that process's memory and its request limits.
 *
 * `XMLHttpRequest` rather than `fetch` for the PUT, because it is the only one
 * that reports upload progress. A silent progress bar on a 2 GB file is the
 * same as no progress bar.
 */
export async function uploadRecording(
  input: { file: File; name: string; participantEmails?: string[] },
  handlers: {
    onPhase?: (phase: UploadPhase) => void
    onProgress?: (percent: number | null) => void
  } = {}
): Promise<{ meetingId: string }> {
  handlers.onPhase?.('preparing')

  const start = await apiSend<DirectUploadStart>(
    'POST',
    '/api/admin/upload-recording/direct/start',
    {
      meeting_name: input.name,
      filename: input.file.name,
      content_type: input.file.type || 'application/octet-stream',
      size_bytes: input.file.size,
      participant_emails: input.participantEmails ?? [],
    }
  )

  handlers.onPhase?.('uploading')
  try {
    await putToStorage(start, input.file, handlers.onProgress)
  } catch (error) {
    // Telling the service the upload died is what stops a half-written object
    // from being processed as if it were a whole meeting.
    await apiSend('POST', '/api/admin/upload-recording/direct/fail', {
      meeting_id: start.meeting_id,
      bot_id: start.bot_id,
      upload_id: start.upload_id,
      s3_key: start.s3_key,
      error: error instanceof Error ? error.message : 'Browser upload failed',
    }).catch(() => undefined)
    throw error
  }

  handlers.onPhase?.('processing')
  handlers.onProgress?.(100)

  const done = await apiSend<{ meeting_id: string }>(
    'POST',
    '/api/admin/upload-recording/direct/complete',
    {
      meeting_id: start.meeting_id,
      bot_id: start.bot_id,
      upload_id: start.upload_id,
      s3_key: start.s3_key,
    }
  )
  return { meetingId: done.meeting_id }
}

function putToStorage(
  start: DirectUploadStart,
  file: File,
  onProgress?: (percent: number | null) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', start.upload_url)
    for (const [header, value] of Object.entries(start.upload_headers ?? {})) {
      request.setRequestHeader(header, value)
    }
    request.upload.onprogress = (event) => {
      onProgress?.(event.lengthComputable ? Math.round((event.loaded / event.total) * 100) : null)
    }
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new Error(`Storage rejected the upload (HTTP ${request.status})`))
    }
    request.onerror = () => reject(new Error('The upload was interrupted.'))
    request.onabort = () => reject(new Error('The upload was cancelled.'))
    request.send(file)
  })
}

/* ---------- Calendars ---------- */

export type CalendarProvider = 'microsoft' | 'google'

export interface CalendarConnection {
  provider: CalendarProvider
  email: string
}

export interface CalendarState {
  connected: boolean
  connections: CalendarConnection[]
  /** How many upcoming meetings the connected calendars can see. */
  upcoming: number
  /** How many of those a recorder is booked for. */
  scheduled: number
}

export async function getCalendars(userId: string): Promise<CalendarState> {
  const body = await apiGet<{
    connected: boolean
    connections?: Array<{ provider: string; email: string }>
    email?: string
    provider?: string
    upcomingMeetings?: number
    meetingsWithBot?: number
  }>(`/auth/status/${encodeURIComponent(userId)}`)

  // The endpoint predates multiple calendars and still answers the old shape
  // for an account with one, so a lone connection is folded into the list
  // rather than handled as a separate case by every caller.
  const connections =
    body.connections?.map((entry) => ({
      provider: entry.provider as CalendarProvider,
      email: entry.email,
    })) ??
    (body.connected && body.email
      ? [{ provider: (body.provider as CalendarProvider) ?? 'microsoft', email: body.email }]
      : [])

  return {
    connected: body.connected,
    connections,
    upcoming: body.upcomingMeetings ?? 0,
    scheduled: body.meetingsWithBot ?? 0,
  }
}

/**
 * Where to send the browser to connect a calendar.
 *
 * The consent round trip returns to the service, not here, so this hands off
 * rather than fetching — the app finds out it worked by asking again when the
 * tab comes back.
 */
export async function calendarConnectUrl(
  userId: string,
  provider: CalendarProvider
): Promise<string> {
  const path =
    provider === 'google'
      ? `/auth/google/oauth-url/${encodeURIComponent(userId)}`
      : `/auth/oauth-url/${encodeURIComponent(userId)}`
  const body = await apiGet<{ oauth_url?: string }>(path)
  // Some deployments answer with a redirect endpoint rather than a URL.
  return body.oauth_url ?? apiUrl(`/auth/connect/${encodeURIComponent(userId)}`)
}

export async function disconnectCalendar(
  userId: string,
  provider: CalendarProvider
): Promise<void> {
  await apiSend('DELETE', `/auth/connection/${encodeURIComponent(userId)}/${provider}`)
}

/* ---------- What gets recorded for ME ---------- */

/**
 * A person's own recording policy, which sits under the organisation's.
 *
 * `inherit` is the default and means "whatever the organisation says" — so the
 * pane has to show what that currently resolves to, or the option is a word
 * with no consequence attached. The other three override it downward or upward
 * for this account's meetings only.
 */
export type PersonalRecordingMode = 'inherit' | RecordingPolicyValue
export type RecordingPolicyValue = 'none' | 'internal' | 'all'

export interface PersonalRecording {
  mode: PersonalRecordingMode
  /** What `inherit` resolves to right now. */
  orgDefault: RecordingPolicyValue
  /** What actually happens, once the two are combined. */
  effective: RecordingPolicyValue
}

function asPolicy(value: unknown, fallback: RecordingPolicyValue): RecordingPolicyValue {
  return value === 'none' || value === 'internal' || value === 'all' ? value : fallback
}

export async function getPersonalRecording(userId: string): Promise<PersonalRecording> {
  const { preferences } = await apiGet<{
    preferences: {
      recording_policy_mode?: string
      org_default_recording_policy?: string
      effective_recording_policy?: string
    }
  }>(`/auth/preferences/${encodeURIComponent(userId)}`)

  const orgDefault = asPolicy(preferences.org_default_recording_policy, 'all')
  return {
    // Anything the server sends that is not one of the three overrides means
    // "follow the organisation" — including the absence of a preference, which
    // is what an account that has never touched this looks like.
    mode: asPolicy(preferences.recording_policy_mode, 'all') === preferences.recording_policy_mode
      ? (preferences.recording_policy_mode as RecordingPolicyValue)
      : 'inherit',
    orgDefault,
    effective: asPolicy(preferences.effective_recording_policy, orgDefault),
  }
}

export async function setPersonalRecording(
  userId: string,
  mode: PersonalRecordingMode
): Promise<void> {
  await apiSend('POST', `/auth/preferences/${encodeURIComponent(userId)}`, {
    recording_policy_mode: mode,
  })
}
